import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { ReminderRule } from "@xynex-money/shared";
import { api, ApiError } from "../api";
import { useCrudResource } from "../hooks/useCrudResource";
import { Table } from "../components/Table";
import type { Column } from "../components/Table";
import { Modal } from "../components/Modal";
import { MoneyInput } from "../components/MoneyInput";
import { DateInput } from "../components/DateInput";
import { fmtDate, fmtINR } from "../fmt";

const RRULES = ReminderRule.options;

interface Reminder {
  id: string;
  title: string;
  amount: number | null;
  dueDay: number;
  rrule: ReminderRule;
  nextDue: string | null;
  autopay: boolean;
  notifyEmail: boolean;
  active: boolean;
}

interface Account {
  id: string;
  name: string;
}

interface FormState {
  title: string;
  amount: number | null;
  dueDay: number;
  rrule: ReminderRule;
  nextDue: string;
  autopay: boolean;
  notifyEmail: boolean;
  active: boolean;
}

const emptyForm: FormState = {
  title: "",
  amount: null,
  dueDay: 1,
  rrule: "monthly",
  nextDue: "",
  autopay: false,
  notifyEmail: true,
  active: true,
};

export function Reminders() {
  const {
    items: reminders,
    loading,
    error,
    setError,
    reload,
    create,
    update,
    remove,
  } = useCrudResource<Reminder>("/api/reminders");
  const [accounts, setAccounts] = useState<Account[]>([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Reminder | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [formError, setFormError] = useState<string | null>(null);

  const [payReminder, setPayReminder] = useState<Reminder | null>(null);
  const [payWithExpense, setPayWithExpense] = useState(false);
  const [payAccountId, setPayAccountId] = useState("");

  useEffect(() => {
    api
      .get<Account[]>("/api/accounts")
      .then(setAccounts)
      .catch((err) => setError(err instanceof ApiError ? err.message : "failed to load accounts"));
  }, [setError]);

  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm);
    setFormError(null);
    setModalOpen(true);
  };
  const openEdit = (r: Reminder) => {
    setEditing(r);
    setForm({
      title: r.title,
      amount: r.amount,
      dueDay: r.dueDay,
      rrule: r.rrule,
      nextDue: r.nextDue ?? "",
      autopay: r.autopay,
      notifyEmail: r.notifyEmail,
      active: r.active,
    });
    setFormError(null);
    setModalOpen(true);
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setFormError(null);
    const body = {
      title: form.title,
      amount: form.amount,
      dueDay: form.dueDay,
      rrule: form.rrule,
      nextDue: form.nextDue,
      autopay: form.autopay,
      notifyEmail: form.notifyEmail,
      active: form.active,
    };
    try {
      if (editing) await update(editing.id, body);
      else await create(body);
      setModalOpen(false);
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : "save failed");
    }
  };

  const onDelete = async (r: Reminder) => {
    if (!confirm(`Delete reminder "${r.title}"?`)) return;
    try {
      await remove(r.id);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "delete failed");
    }
  };

  const openPay = (r: Reminder) => {
    setPayReminder(r);
    setPayWithExpense(false);
    setPayAccountId(accounts[0]?.id ?? "");
  };

  const onMarkPaid = async () => {
    if (!payReminder) return;
    try {
      await api.post(`/api/reminders/${payReminder.id}/paid`, {
        ...(payWithExpense && payAccountId ? { createExpense: { accountId: payAccountId } } : {}),
      });
      setPayReminder(null);
      reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "mark-paid failed");
    }
  };

  const columns: Column<Reminder>[] = [
    { key: "title", header: "Title" },
    { key: "amount", header: "Amount", align: "right", render: (r) => fmtINR(r.amount) },
    { key: "nextDue", header: "Next due", render: (r) => fmtDate(r.nextDue) },
    { key: "rrule", header: "Repeats" },
    { key: "active", header: "Active", render: (r) => (r.active ? "Yes" : "No") },
    {
      key: "actions",
      header: "",
      render: (r) => (
        <span className="actions-inline">
          {r.active && r.nextDue && (
            <button type="button" onClick={() => openPay(r)}>
              Mark paid
            </button>
          )}
          <button type="button" onClick={() => openEdit(r)}>
            Edit
          </button>
          <button type="button" className="danger" onClick={() => onDelete(r)}>
            Delete
          </button>
        </span>
      ),
    },
  ];

  return (
    <div>
      <h1>Reminders</h1>
      {error && <div className="error-text">{error}</div>}
      <div className="toolbar">
        <button className="primary" type="button" onClick={openAdd}>
          Add reminder
        </button>
      </div>
      <Table columns={columns} rows={reminders} rowKey={(r) => r.id} emptyText={loading ? "Loading…" : "No reminders yet."} />

      {modalOpen && (
        <Modal title={editing ? "Edit reminder" : "Add reminder"} onClose={() => setModalOpen(false)}>
          <form onSubmit={onSubmit} className="modal-body">
            <div className="field">
              <label htmlFor="r-title">Title</label>
              <input id="r-title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            </div>
            <div className="form-grid">
              <div className="field">
                <label htmlFor="r-amount">Amount</label>
                <MoneyInput value={form.amount} onChange={(v) => setForm({ ...form, amount: v })} />
              </div>
              <div className="field">
                <label htmlFor="r-dueday">Due day (1-31)</label>
                <input
                  id="r-dueday"
                  type="number"
                  min={1}
                  max={31}
                  required
                  value={form.dueDay}
                  onChange={(e) => setForm({ ...form, dueDay: Number(e.target.value) })}
                />
              </div>
              <div className="field">
                <label htmlFor="r-rrule">Repeats</label>
                <select id="r-rrule" value={form.rrule} onChange={(e) => setForm({ ...form, rrule: e.target.value as FormState["rrule"] })}>
                  {RRULES.map((rr) => (
                    <option key={rr} value={rr}>
                      {rr}
                    </option>
                  ))}
                </select>
              </div>
              <div className="field">
                <label htmlFor="r-nextdue">Next due</label>
                <DateInput value={form.nextDue} onChange={(v) => setForm({ ...form, nextDue: v })} required />
              </div>
            </div>
            <div className="field">
              <label style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                <input type="checkbox" checked={form.autopay} onChange={(e) => setForm({ ...form, autopay: e.target.checked })} />
                Autopay
              </label>
            </div>
            <div className="field">
              <label style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                <input
                  type="checkbox"
                  checked={form.notifyEmail}
                  onChange={(e) => setForm({ ...form, notifyEmail: e.target.checked })}
                />
                Notify by email
              </label>
            </div>
            <div className="field">
              <label style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
                Active
              </label>
            </div>
            {formError && <div className="error-text">{formError}</div>}
            <div className="modal-actions">
              <button type="button" onClick={() => setModalOpen(false)}>
                Cancel
              </button>
              <button className="primary" type="submit">
                Save
              </button>
            </div>
          </form>
        </Modal>
      )}

      {payReminder && (
        <Modal title={`Mark "${payReminder.title}" paid`} onClose={() => setPayReminder(null)}>
          <div className="modal-body">
            <div className="field">
              <label style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                <input type="checkbox" checked={payWithExpense} onChange={(e) => setPayWithExpense(e.target.checked)} />
                Also record an expense transaction
              </label>
            </div>
            {payWithExpense && (
              <div className="field">
                <label htmlFor="pay-account">Pay from account</label>
                <select id="pay-account" value={payAccountId} onChange={(e) => setPayAccountId(e.target.value)}>
                  {accounts.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="modal-actions">
              <button type="button" onClick={() => setPayReminder(null)}>
                Cancel
              </button>
              <button className="primary" type="button" onClick={onMarkPaid}>
                Confirm
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
